import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import * as FileSaver from 'file-saver'
import { Observable } from 'rxjs'

import {
  NUMBER_FIVE_HUNDRED,
  NUMBER_TEN,
} from '../../../constants/constant.numbers'
import {
  NODE_FUND_LITERATURE,
  NODE_FUND_LITERATURE_DOWNLOAD_PDF,
} from '../../../constants/constant.urls'
import { GetURL } from '../../../constants/get-url'
import { UtilityService } from '../utility-service/utility-service.service'

@Injectable({
  providedIn: 'root',
})
export class FundLiteratureContentService {
  private fundLiteratureData: any
  private fundLitMap: Map<string, string> = new Map<string, string>()
  finalPdfList = []
  fileCount = NUMBER_TEN
  fileSaver: any
  constructor(
    private readonly http: HttpClient,
    private readonly utilityService: UtilityService,
  ) {
    this.fileSaver = FileSaver
  }

  public getFD3FundLiteratureContent(): Observable<any> {
    return this.http.get<any>(GetURL(`${NODE_FUND_LITERATURE}`))
  }

  getFundLiteratureData() {
    return this.fundLiteratureData
  }

  setFundLiteratureData(data: any) {
    this.fundLiteratureData = data
  }

  setFactSheetData() {
    const { STRATEGY_DOCUMENT } = this.fundLiteratureData

    STRATEGY_DOCUMENT.forEach(({ FUND_NUMBERS, FACT_SHEET_URLS }) => {
      const fundNumbers =
        this.utilityService.convertLitDataStrToArray(FUND_NUMBERS)
      const factSheet =
        this.utilityService.convertLitDataStrToArray(FACT_SHEET_URLS)

      fundNumbers.forEach((fundNumber, index) => {
        this.fundLitMap.set(fundNumber, factSheet[index])
      })
    })
  }

  getFactSheetData(): Map<string, string> {
    return this.fundLitMap
  }

  savePdf(pdfList: any[]): Observable<void> {
    return new Observable((subscriber) => {
      this.fetchPdf(pdfList).then((r) => subscriber.next(r))
    })
  }

  async fetchPdf(pdfList: any[]): Promise<void> {
    const fileCount = pdfList.length
    let pdfDownloadCompleted = 0
    const handleResponse = (blobResponse: any, item: any) => {
      const fileName = this.getFileName(item)
      this.fileSaver.saveAs(blobResponse, fileName)
    }
    for (const item of pdfList) {
      if (fileCount >= this.fileCount) {
        await this.pause()
      }
      if (this.finalPdfList !== null && this.finalPdfList.includes(item.url)) {
        const updatedUrl = this.appendHttpsProtocol(item.url)
        const pdfUrl = GetURL(
          `${NODE_FUND_LITERATURE_DOWNLOAD_PDF}?pdfUrl=${updatedUrl}`,
        )
        this.http.request('GET', pdfUrl, { responseType: 'blob' }).subscribe({
          next: async (blobResponse) => {
            handleResponse(blobResponse, item)
            pdfDownloadCompleted++
            if (pdfDownloadCompleted === fileCount) {
              await this.handleDownloadCompletion()
            }
          },
          error: async (error) => {
            console.error('Error while fetching the pdf: ', error)
            pdfDownloadCompleted++
            if (pdfDownloadCompleted === fileCount) {
              await this.handleDownloadCompletion()
            }
          },
        })
      } else {
        throw new Error('Wrong url added')
      }
    }
  }

  private appendHttpsProtocol(pdfUrl: string): string {
    if (!pdfUrl.split('/')[0].includes('http')) {
      pdfUrl = 'https://' + pdfUrl
    }
    return pdfUrl
  }

  private extractFileExtension(url: string): string {
    try {
      const splitUrl = url.split('/')
      const fileNameWithExtension = splitUrl[splitUrl.length - 1]
      const splitFileName = fileNameWithExtension.split('.')
      if (splitFileName.length > 1) {
        return splitFileName[splitFileName.length - 1]
      } else {
        throw new Error('No file extension found')
      }
    } catch (e) {
      console.error('Error extracting file extension:', e)
      return 'pdf'
    }
  }

  getFileName(item: { url: string; fileName: string; key: string }): string {
    try {
      const fileExtension = this.extractFileExtension(item.url)
      return `${item.fileName.replace(/ /g, '_')}_${item.key}.${fileExtension}`
    } catch (e) {
      console.error('Error generating file name:', e)
      return 'VanguardFundLiterature.pdf'
    }
  }

  async pause() {
    return new Promise((resolve) => {
      setTimeout(resolve, NUMBER_FIVE_HUNDRED)
    })
  }

  private handleDownloadCompletion(): Promise<{
    isDownloadCompleted: boolean
  }> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ isDownloadCompleted: true })
      }, NUMBER_FIVE_HUNDRED)
    })
  }
}
